import type { WorkerRequest, WorkerResponseMessage } from './protocol.js';

export type ProgressHandler = (done: number, total: number) => void;

/** Thin promise-based RPC layer on top of a dedicated Worker using the {id, op, payload} protocol. */
export class WorkerClient {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<number, { resolve: (v: any) => void; reject: (e: Error) => void; onProgress?: ProgressHandler }>();

  constructor(worker: Worker) {
    this.worker = worker;
    this.worker.addEventListener('message', (event: MessageEvent<WorkerResponseMessage>) => {
      const msg = event.data;
      const entry = this.pending.get(msg.id);
      if (!entry) return;
      if (msg.type === 'progress') {
        entry.onProgress?.(msg.done, msg.total);
        return;
      }
      this.pending.delete(msg.id);
      if (msg.type === 'error') entry.reject(new Error(msg.message));
      else entry.resolve(msg.result);
    });
    this.worker.addEventListener('error', (event) => {
      for (const [, entry] of this.pending) entry.reject(new Error(event.message || 'Worker error'));
      this.pending.clear();
    });
  }

  call<TResult = unknown>(op: string, payload: unknown, transfer: Transferable[] = [], onProgress?: ProgressHandler): Promise<TResult> {
    const id = this.nextId++;
    return new Promise<TResult>((resolve, reject) => {
      this.pending.set(id, { resolve, reject, onProgress });
      const request: WorkerRequest = { id, op, payload };
      this.worker.postMessage(request, transfer);
    });
  }

  terminate(): void {
    this.worker.terminate();
    this.pending.clear();
  }
}
